const bufferCache = new Map<string, AudioBuffer>();
const loadingPromises = new Map<string, Promise<AudioBuffer>>();

let audioContext: AudioContext | null = null;
let isSoundEnabledCache: boolean = true;

let currentMusicSource: AudioBufferSourceNode | null = null;
let musicGainNode: GainNode | null = null;
let sfxGainNode: GainNode | null = null;

let activeMusicRequestId = 0;

try {
  isSoundEnabledCache = JSON.parse(
    localStorage.getItem('setSoundEffect') || 'true',
  );
} catch {
  isSoundEnabledCache = true;
}

export const stopBackgroundMusic = (): void => {
  if (currentMusicSource) {
    try {
      currentMusicSource.stop();
      currentMusicSource.disconnect();
    } catch (e) {
      console.error('Error stopping music source:', e);
    }
  }
  currentMusicSource = null;
};
export const updateSoundSettings = (enabled: boolean): void => {
  isSoundEnabledCache = enabled;
  localStorage.setItem('setSoundEffect', JSON.stringify(enabled));

  if (enabled) {
    const ctx = getAudioContext();

    if (!ctx) return;

    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }

    playBackgroundMusic('main_music');
  } else {
    stopBackgroundMusic();
  }
};

const getAudioContext = (): AudioContext | null => {
  if (!audioContext) {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;

    if (!AudioContextClass) {
      return null;
    }

    audioContext = new AudioContextClass();
  }

  return audioContext;
};

const loadBuffer = async (
  ctx: AudioContext,
  key: string,
): Promise<AudioBuffer> => {
  if (bufferCache.has(key)) return bufferCache.get(key)!;

  if (loadingPromises.has(key)) return loadingPromises.get(key)!;

  const soundUrl = `/sounds/${key}.wav`;

  const promise = (async (): Promise<AudioBuffer> => {
    try {
      const response = await fetch(soundUrl);

      if (!response.ok) throw new Error(`Status ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      const buffer = await ctx.decodeAudioData(arrayBuffer);

      bufferCache.set(key, buffer);

      return buffer;
    } finally {
      loadingPromises.delete(key);
    }
  })();

  loadingPromises.set(key, promise);

  return promise;
};

export const playSound = async (
  key: string,
  volume: number = 0.7,
): Promise<void> => {
  if (!isSoundEnabledCache) return;

  const ctx = getAudioContext();

  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  try {
    const buffer = await loadBuffer(ctx, key);

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    if (!sfxGainNode) {
      sfxGainNode = ctx.createGain();
      sfxGainNode.connect(ctx.destination);
    }

    sfxGainNode.gain.value = volume;
    source.connect(sfxGainNode);

    source.start(0);
  } catch (error) {
    console.error(`Audio Error (${key}):`, error);
  }
};
export const playBackgroundMusic = async (
  key: string,
  volume: number = 0.4,
): Promise<void> => {
  if (!isSoundEnabledCache) return;

  const requestId = ++activeMusicRequestId;
  const ctx = getAudioContext();

  if (!ctx) return;

  stopBackgroundMusic();

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  try {
    const buffer = await loadBuffer(ctx, key);

    if (requestId !== activeMusicRequestId) return;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.loop = true;

    if (!musicGainNode) {
      musicGainNode = ctx.createGain();
      musicGainNode.connect(ctx.destination);
    }

    musicGainNode.gain.value = volume;
    source.connect(musicGainNode);
    source.start(0);

    currentMusicSource = source;
  } catch (error) {
    console.error(`Music Error (${key}):`, error);
  }
};
